import { useEffect, useRef, useState } from "react";
import { useAuthStore, type UsernameStatus } from "../../store/useAuthStore.js";
import { useI18n } from "../../i18n/index.js";

interface Props {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}

export function UsernameField({ value, onChange, disabled }: Props) {
  const checkUsername = useAuthStore((s) => s.checkUsername);
  const [status, setStatus] = useState<UsernameStatus>("idle");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { t } = useI18n();

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (value.length === 0) {
      setStatus("idle");
      return;
    }

    setStatus("checking");

    debounceRef.current = setTimeout(async () => {
      const result = await checkUsername(value);
      setStatus(result);
    }, 500);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value, checkUsername]);

  const hint = {
    idle:      <span className="text-white/40 text-xs">{t("username.idle")}</span>,
    checking:  <span className="text-white/50 text-xs">{t("username.checking")}</span>,
    available: <span className="text-green-400 text-xs">{t("username.available")}</span>,
    taken:     <span className="text-red-400 text-xs">{t("username.taken")}</span>,
    invalid:   <span className="text-red-400 text-xs">{t("username.invalid")}</span>,
  }[status];

  const borderColor = {
    idle:      "border-white/20",
    checking:  "border-white/30",
    available: "border-green-500/60",
    taken:     "border-red-500/60",
    invalid:   "border-red-500/60",
  }[status];

  return (
    <div className="flex flex-col gap-1">
      <label className="text-sm text-white/70">{t("username.label")}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        maxLength={10}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        placeholder={t("username.placeholder")}
        className={[
          "w-full px-3 py-2 rounded-lg bg-white/10 border text-white placeholder-white/30",
          "focus:outline-none focus:ring-2 focus:ring-white/30 transition-colors",
          "disabled:opacity-50",
          borderColor,
        ].join(" ")}
      />
      <div className="min-h-[1.25rem]">{hint}</div>
    </div>
  );
}
